'use client';

import { useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { Button, ButtonType } from '@nx-frontend/shared/ui/button';
import { $ModalStore, closeModalEvent } from '@nx-frontend/shared/ui/modal';
import { PopupType, TrialWelcomePopupPayload } from '@nx-frontend/shared/types';
import { GetOpenTrialWelcomePopupProps } from '@nx-frontend/shared/types';

export const getOpenTrialWelcomePopup = ({ openModal, payload }: GetOpenTrialWelcomePopupProps) => {
  openModal({
    content: PopupType.TRIAL_WELCOME,
    showCloseButton: false,
    payload,
  });
};

export function TrialWelcomePopup() {
  const [modalStore, closeModal] = useUnit([$ModalStore, closeModalEvent]);

  const t = useTranslations('webnotes');

  const nimTrialWelcomeRef = useRef<HTMLDivElement>(null);

  let animated = false;

  const modal = modalStore.find((modal) => modal.payload && modal.payload.payloadType === PopupType.TRIAL_WELCOME);
  const payload = modal?.payload as TrialWelcomePopupPayload;

  if (payload && payload.animated) {
    animated = payload.animated;
  }

  const onWowConfirmClick = useCallback(async () => {
    closeModal();
    window.location.href = window.location.href
      .replace('trial_welcome=true', 'trial_welcome=false')
      .replace('fromCall=true', 'fromCall=false')
      .replace('show_trial_popup=1', 'show_trial_popup=0')
      .replace('trial_wow_popup=1', '')
      .replace('from=wizard', '');
  }, [closeModal]);

  const onWelcomeConfirmClick = useCallback(async () => {
    closeModal();
    window.location.href = window.location.href.split('?')[0];
  }, [closeModal]);

  const showWowTrialPopup = () => (
    <div
      data-cy="trial-welcome-popup"
      className="px-[124px] py-[100px] w-[1010px] min-w-[1010px] min-h-[564px] relative nimbus-popup-content mx-1"
      ref={nimTrialWelcomeRef}
    >
      <div className="flex flex-col items-end">
        <div className="flex">
          <div className="mr-[100px]">
            <div className="items-center basis-full w-full text-4xl font-semibold mb-[30px] relative">
              <div className="z-[1] relative">{t('webnotes-wow_lucky_today')}</div>
              <img
                src="/assets/img/trial-welcome-text-bg.svg"
                width={101}
                height={45}
                className="min-w-[101px] min-h-[45px] absolute top-0 left-0 z-0"
                alt=""
              />
            </div>
            <div className="text-base text-justify mb-[30px] w-[340px]">
              {t.rich('webnotes-lucky_today_description', {
                strong: (chunk) => <strong>{chunk}</strong>,
              })}
            </div>
            <div className="items-center gap-2 w-full">
              <Button
                type={ButtonType.FLAT}
                onClick={onWowConfirmClick}
                bg="bg-[#002730] hover:bg-[#002730] active:bg-[#002730]"
                radius="rounded"
                isFull={true}
              >
                {t('webnotes-lets_start')}
              </Button>
            </div>
          </div>
          <div className="items-top">
            <img
              src="/assets/img/trial-welcome-static.svg"
              width={322}
              height={318}
              className="min-w-[322px] min-h-[318px]"
              alt=""
            />
          </div>
        </div>
      </div>
    </div>
  );

  const showWelcomeTrialPopup = () => (
    <div
      data-cy="trial-welcome-popup"
      className="w-[713px] min-w-[713px] min-h-[495px] relative nimbus-popup-content mx-1"
      ref={nimTrialWelcomeRef}
    >
      <div className="flex flex-col items-end">
        <div className="flex flex-col justify-items-center w-full">
          <div className="flex flex-col items-center justify-center">
            <div className="inline-block text-4xl font-semibold mb-[35px] mt-[20px] relative">
              <div className="z-[1] relative justify-self-center">Welcome to Your Trial</div>
              <img
                src="/assets/img/trial-welcome-text-bg-long.svg"
                width={169}
                height={59}
                className="min-w-[169px] min-h-[59px] absolute -top-[7px] -left-[8px] z-0"
                alt=""
              />
            </div>
            <div className="inline-block text-base text-center justify-self-center mb-[35px] max-w-[532px]">
              Your trial has begun. Explore our features and let us know what you think. You can decide later whether to
              continue or cancel. Thank you for choosing us!
            </div>
          </div>
          <div className="flex items-top justify-center mb-[35px]">
            <video
              width={248}
              height={186}
              src="/assets/video/welcome-video.mp4"
              poster="/assets/img/welcome.gif"
              className="min-w-[248px] min-h-[186px]"
              autoPlay={true}
              muted={true}
              loop={true}
            ></video>
          </div>
          <div className="flex items-center gap-2 w-full justify-center">
            <Button
              type={ButtonType.FLAT}
              onClick={onWelcomeConfirmClick}
              bg="bg-[#002730] hover:bg-[#002730] active:bg-[#002730]"
              radius="rounded"
            >
              Let's start!
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return animated ? showWelcomeTrialPopup() : showWowTrialPopup();
}
